import { useId, useState, type ComponentProps, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cx } from './cx';
import { controlClass } from './control-styles';
import { useFieldControl } from './field-context';

interface ValidationProps {
  /** Overrides the enclosing FormField's state. */
  invalid?: boolean;
  valid?: boolean;
}

export interface InputProps extends Omit<ComponentProps<'input'>, 'size'>, ValidationProps {
  /** Decorative icon inside the field, before the text. */
  icon?: ReactNode;
}

/** Text-like input (text, email, search, url, tel, number…). Use inside FormField for a label. */
export function Input({ invalid, valid, icon, className, type = 'text', ...props }: InputProps) {
  const f = useFieldControl({ ...props, invalid, valid });
  const input = (
    <input
      type={type}
      {...props}
      id={f.id}
      aria-describedby={f['aria-describedby']}
      aria-invalid={f['aria-invalid']}
      required={f.required}
      disabled={f.disabled}
      className={cx(
        controlClass(f.invalid, f.valid),
        'read-only:bg-surface-muted',
        'h-control',
        icon && 'ps-8',
        className,
      )}
    />
  );
  if (!icon) return input;
  return (
    <span className="relative block">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 start-2.5 flex items-center text-ink-muted"
      >
        {icon}
      </span>
      {input}
    </span>
  );
}

export interface PasswordInputProps extends Omit<InputProps, 'type' | 'icon'> {
  showLabel?: string;
  hideLabel?: string;
}

/**
 * Password with a show/hide toggle (a real button with aria-pressed, so it is keyboard- and
 * screen-reader-operable). autocomplete defaults to current-password.
 */
export function PasswordInput({
  showLabel = 'Show password',
  hideLabel = 'Hide password',
  className,
  autoComplete = 'current-password',
  ...props
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const f = useFieldControl(props);
  return (
    <span className="relative block">
      <Input
        {...props}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        className={cx('pe-10', className)}
      />
      <button
        type="button"
        aria-pressed={visible}
        aria-controls={f.id}
        aria-label={visible ? hideLabel : showLabel}
        title={visible ? hideLabel : showLabel}
        disabled={f.disabled}
        onClick={() => setVisible((v) => !v)}
        className="absolute inset-y-0 end-0 flex w-9 items-center justify-center text-ink-muted hover:text-ink disabled:opacity-60"
      >
        {visible ? (
          <EyeOff aria-hidden="true" className="size-4" />
        ) : (
          <Eye aria-hidden="true" className="size-4" />
        )}
      </button>
    </span>
  );
}

export interface TextareaProps extends ComponentProps<'textarea'>, ValidationProps {}

export function Textarea({ invalid, valid, className, rows = 4, ...props }: TextareaProps) {
  const f = useFieldControl({ ...props, invalid, valid });
  return (
    <textarea
      rows={rows}
      {...props}
      id={f.id}
      aria-describedby={f['aria-describedby']}
      aria-invalid={f['aria-invalid']}
      required={f.required}
      disabled={f.disabled}
      className={cx(
        controlClass(f.invalid, f.valid),
        'read-only:bg-surface-muted',
        'min-h-control py-1.5',
        className,
      )}
    />
  );
}

export interface CheckboxProps extends Omit<ComponentProps<'input'>, 'type'> {
  label: ReactNode;
  hint?: ReactNode;
}

/** Native checkbox + its own label (not for use inside FormField). 24px target (WCAG 2.5.8). */
export function Checkbox({ label, hint, id, className, disabled, ...props }: CheckboxProps) {
  const auto = `cb${useStableId()}`;
  const cid = id ?? auto;
  return (
    <div className={cx('flex items-start gap-2', className)}>
      <span className="flex size-6 shrink-0 items-center justify-center">
        <input
          type="checkbox"
          id={cid}
          disabled={disabled}
          aria-describedby={hint ? `${cid}-hint` : undefined}
          className="size-4 accent-primary disabled:cursor-not-allowed"
          {...props}
        />
      </span>
      <span className="flex flex-col pt-0.5">
        <label htmlFor={cid} className={cx('text-sm text-ink', disabled && 'opacity-60')}>
          {label}
        </label>
        {hint && (
          <span id={`${cid}-hint`} className="text-xs text-ink-muted">
            {hint}
          </span>
        )}
      </span>
    </div>
  );
}

function useStableId() {
  return useId().replace(/:/g, '');
}
